import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { API_BASE_URL } from '../config/api';
import { useAuth } from './AuthContext';

export const APP_LABELS: Record<string, string> = {
  platform: 'Admin',
  inventory: 'Inventory',
  sales: 'Sales',
  restaurant: 'Restaurant',
  carRental: 'Car Rental',
  automobile: 'AutoMobile',
  accounting: 'Accounting',
};

export const APP_ICONS: Record<string, string> = {
  platform: '⚙️',
  inventory: '📦',
  sales: '🛒',
  restaurant: '🍽️',
  automobile: '🔧',
  accounting: '📒',
};

interface AppAccessContextType {
  availableApps: string[];
  currentApp: string | null;
  switchApp: (appCode: string) => void;
  refreshApps: () => Promise<void>;
}

const AppAccessContext = createContext<AppAccessContextType | null>(null);

const LAST_APP_KEY = 'last_app';

/** Read the company's moduleCode directly from localStorage so this stays dep-free. */
function getStoredModuleCode(): string | null {
  try {
    const raw = localStorage.getItem('company');
    return raw ? (JSON.parse(raw) as { moduleCode?: string }).moduleCode ?? null : null;
  } catch {
    return null;
  }
}

export const AppAccessProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { token, isAuthenticated, user } = useAuth();
  const [availableApps, setAvailableApps] = useState<string[]>([]);
  const [currentApp, setCurrentApp] = useState<string | null>(null);

  const applyApps = useCallback((apps: string[]) => {
    setAvailableApps(apps);
    const remembered = localStorage.getItem(LAST_APP_KEY);
    // Prefer the company's business-vertical app (e.g. 'restaurant') over 'platform'
    // so users land on a useful sidebar by default instead of an empty one.
    const moduleCode = getStoredModuleCode();
    const preferred = apps.find(a => a === moduleCode) ?? apps.find(a => a !== 'platform') ?? apps[0] ?? null;
    const resolved = remembered && apps.includes(remembered) ? remembered : preferred;
    setCurrentApp(resolved);
    if (resolved) localStorage.setItem(LAST_APP_KEY, resolved);
  }, []);

  const refreshApps = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/auth/my-apps`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        applyApps(json?.data ?? []);
      }
    } catch {}
  }, [token, applyApps]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setAvailableApps([]);
      setCurrentApp(null);
      return;
    }
    // Prefer the apps already on the auth user object (set at login / /me refresh)
    // to avoid an extra round-trip. Fall back to an explicit fetch if missing.
    if (user?.allowedApps && user.allowedApps.length > 0) {
      applyApps(user.allowedApps);
    } else {
      refreshApps();
    }
  }, [isAuthenticated, token, user?.allowedApps]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchApp = useCallback((appCode: string) => {
    setCurrentApp(appCode);
    localStorage.setItem(LAST_APP_KEY, appCode);
  }, []);

  return (
    <AppAccessContext.Provider value={{ availableApps, currentApp, switchApp, refreshApps }}>
      {children}
    </AppAccessContext.Provider>
  );
};

export function useAppAccess(): AppAccessContextType {
  const ctx = useContext(AppAccessContext);
  if (!ctx) throw new Error('useAppAccess must be used inside AppAccessProvider');
  return ctx;
}
